import { useCallback, useEffect, useRef, useState } from 'react';
import type { RebuildSession, ScriptEdit, SelectedRecommendation } from '@/types/rebuild';
import {
  exportRebuild,
  getRebuildDocument,
  getRebuildSession,
  getRebuildStatus,
  patchRebuildEdit,
  saveRebuildContent,
  startRebuild,
} from '@/services/rebuildService';
import type { AvailableMetricRecommendations } from '@/types/rebuild';

export function useRebuildSession(analysisSessionId: string | undefined) {
  const [session, setSession] = useState<RebuildSession | null>(null);
  const [available, setAvailable] = useState<AvailableMetricRecommendations[]>([]);
  const [originalText, setOriginalText] = useState('');
  const [currentText, setCurrentText] = useState('');
  const [edits, setEdits] = useState<ScriptEdit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  const pollUntilComplete = useCallback(async () => {
    if (!analysisSessionId) return;
    setPolling(true);
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const st = await getRebuildStatus(analysisSessionId);
        if (st.status === 'error') {
          setError(st.error ?? 'Rebuild failed');
          stopPolling();
          return;
        }
        if (st.status === 'complete') {
          stopPolling();
          const doc = await getRebuildDocument(analysisSessionId);
          setOriginalText(doc.original_text);
          setCurrentText(doc.current_text);
          setEdits(doc.edits);
          setSession(doc.session);
        } else {
          setSession((prev) =>
            prev
              ? {
                  ...prev,
                  status: (st.status as RebuildSession['status']) ?? prev.status,
                  progress: st.progress ?? prev.progress,
                }
              : prev,
          );
        }
      } catch {
        /* keep polling */
      }
    }, 2500);
  }, [analysisSessionId, stopPolling]);

  const loadSession = useCallback(async () => {
    if (!analysisSessionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getRebuildSession(analysisSessionId);
      setSession(data.session);
      setAvailable(data.available_recommendations);
      if (
        data.session.status === 'complete' ||
        data.session.status === 'in_progress' ||
        data.session.edits?.length
      ) {
        try {
          const doc = await getRebuildDocument(analysisSessionId);
          setOriginalText(doc.original_text);
          setCurrentText(doc.current_text);
          setEdits(doc.edits);
        } catch {
          /* document not ready yet */
        }
      }
      if (data.session.status === 'in_progress') {
        void pollUntilComplete();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load rebuild session');
    } finally {
      setLoading(false);
    }
  }, [analysisSessionId, pollUntilComplete]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const runRebuild = useCallback(
    async (selected: SelectedRecommendation[]) => {
      if (!analysisSessionId) return;
      setError(null);
      try {
        await startRebuild(analysisSessionId, selected);
        setSession((prev) =>
          prev
            ? { ...prev, status: 'in_progress', selected_recommendations: selected }
            : prev,
        );
        await pollUntilComplete();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to start rebuild');
      }
    },
    [analysisSessionId, pollUntilComplete],
  );

  const toggleEdit = useCallback(
    async (editId: string, status: 'applied' | 'reverted') => {
      if (!analysisSessionId) return;
      const updated = await patchRebuildEdit(analysisSessionId, editId, status);
      setSession(updated);
      setEdits(updated.edits);
      const doc = await getRebuildDocument(analysisSessionId);
      setCurrentText(doc.current_text);
    },
    [analysisSessionId],
  );

  const saveContent = useCallback(
    async (text: string) => {
      if (!analysisSessionId) return;
      await saveRebuildContent(analysisSessionId, text);
      setCurrentText(text);
    },
    [analysisSessionId],
  );

  const finalizeExport = useCallback(async () => {
    if (!analysisSessionId) return;
    const updated = await exportRebuild(analysisSessionId);
    setSession(updated);
  }, [analysisSessionId]);

  return {
    session,
    available,
    originalText,
    currentText,
    setCurrentText,
    edits,
    loading,
    error,
    polling,
    loadSession,
    runRebuild,
    toggleEdit,
    saveContent,
    finalizeExport,
  };
}
